/**
 * Patient General Info - Professional patient information display
 */
import React from 'react';
import { format } from 'date-fns';
import { ro } from 'date-fns/locale';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { 
  User, 
  Phone, 
  Mail, 
  MapPin, 
  Calendar, 
  Droplets, 
  Edit, 
  Ruler,
  AlertTriangle,
  FileText,
  Users,
  Activity,
  Scale,
  Stethoscope,
  Clock,
  Heart
} from 'lucide-react';
import { calculateAge, calculateBMI, getValueColor, NORMAL_VALUES } from './utils';

export const PatientGeneralInfo = ({ 
  patient, 
  consultations, 
  diagnoses, 
  canEdit, 
  onEditClick 
}) => {
  if (!patient) return null;

  const getDiagnosisName = (diagId) => {
    const diagnosis = diagnoses.find(d => d.id === diagId);
    if (diagnosis) {
      return `${diagnosis.code}: ${diagnosis.name}`;
    }
    return diagId;
  };

  // Get latest consultation data
  const latestConsultation = consultations[0];
  const latestWeight = latestConsultation?.weight;
  const bmi = calculateBMI(patient.height, latestWeight);
  
  // Get latest lab values
  const latestGlycemia = latestConsultation?.glycemia;
  const latestHbA1c = latestConsultation?.hba1c;
  const latestHDL = latestConsultation?.hdl;
  const latestLDL = latestConsultation?.ldl;
  const latestTriglycerides = latestConsultation?.triglycerides;

  // Info item component for consistent styling
  const InfoItem = ({ label, value, icon: Icon, className = "" }) => (
    <div className="flex items-start gap-3">
      {Icon && <Icon className="w-4 h-4 text-slate-400 mt-0.5 flex-shrink-0" />}
      <div className="min-w-0 flex-1">
        <p className="text-xs font-medium text-slate-400 uppercase tracking-wider">{label}</p>
        <p className={`text-sm font-medium text-slate-800 ${className}`}>{value || <span className="text-slate-300">-</span>}</p>
      </div>
    </div>
  );

  // Section header component
  const SectionHeader = ({ icon: Icon, title }) => (
    <div className="flex items-center gap-2 pb-3 mb-4 border-b border-slate-100">
      <Icon className="w-4 h-4 text-slate-500" />
      <h3 className="text-sm font-semibold text-slate-600 uppercase tracking-wider">{title}</h3>
    </div>
  );

  return (
    <Card className="shadow-sm" data-testid="patient-general-info">
      {/* Header */}
      <div className="flex items-center justify-between px-6 py-4 border-b bg-slate-50/50">
        <div className="flex items-center gap-2">
          <User className="w-5 h-5 text-slate-500" />
          <h2 className="text-base font-semibold text-slate-700">Informații Generale</h2>
        </div>
      </div>

      <CardContent className="p-6">
        {/* Main 3-column grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          
          {/* Column 1: Personal Data */}
          <div>
            <SectionHeader icon={User} title="Date Personale" />
            <div className="space-y-4">
              <div>
                <p className="text-xs font-medium text-slate-400 uppercase tracking-wider">Nume complet</p>
                <p className="text-base font-semibold text-slate-900">{patient.last_name} {patient.first_name}</p>
              </div>
              
              <InfoItem label="CNP" value={patient.cnp} className="font-mono" />
              
              <div className="flex items-start gap-3">
                <Calendar className="w-4 h-4 text-slate-400 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-xs font-medium text-slate-400 uppercase tracking-wider">Data nașterii</p>
                  <p className="text-sm font-medium text-slate-800">
                    {format(new Date(patient.date_of_birth), 'd MMMM yyyy', { locale: ro })}
                  </p>
                  <p className="text-xs text-slate-500">{calculateAge(patient.date_of_birth)} ani</p>
                </div>
              </div>
              
              <InfoItem label="Gen" value={patient.gender === 'M' ? 'Masculin' : 'Feminin'} />

              {patient.blood_type && (
                <div className="flex items-start gap-3">
                  <Droplets className="w-4 h-4 text-red-400 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-xs font-medium text-slate-400 uppercase tracking-wider">Grupa sanguină</p>
                    <p className="text-sm font-bold text-red-600">{patient.blood_type}</p>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Column 2: Contact */}
          <div>
            <SectionHeader icon={Phone} title="Contact" />
            <div className="space-y-4">
              <InfoItem icon={Phone} label="Telefon" value={patient.phone} />
              <InfoItem icon={Mail} label="Email" value={patient.email} className="break-all" />
              <InfoItem icon={MapPin} label="Adresă" value={patient.address} />

              {/* Emergency Contact */}
              <div className="pt-3 mt-3 border-t border-slate-100">
                <div className="flex items-center gap-2 mb-3">
                  <Users className="w-4 h-4 text-orange-500" />
                  <p className="text-xs font-semibold text-slate-600 uppercase tracking-wider">Contact de urgență</p>
                </div>
                {patient.emergency_contact_name || patient.emergency_contact ? (
                  <div className="bg-orange-50 rounded-lg p-3 border border-orange-100">
                    <p className="text-sm font-medium text-orange-800">
                      {patient.emergency_contact_name || patient.emergency_contact}
                    </p>
                    {(patient.emergency_contact_phone || patient.emergency_phone) && (
                      <p className="text-sm text-orange-600 mt-0.5">
                        {patient.emergency_contact_phone || patient.emergency_phone}
                      </p>
                    )}
                  </div>
                ) : (
                  <p className="text-xs text-slate-400 italic">Necompletat</p>
                )}
              </div>
            </div>
          </div>

          {/* Column 3: Medical Data */}
          <div>
            <SectionHeader icon={Heart} title="Date Medicale" />
            <div className="space-y-4">
              {/* Height & Weight in a row */}
              <div className="grid grid-cols-2 gap-4">
                <div className="flex items-start gap-3">
                  <Ruler className="w-4 h-4 text-slate-400 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-xs font-medium text-slate-400 uppercase tracking-wider">Înălțime</p>
                    <p className="text-sm font-medium text-slate-800">
                      {patient.height ? `${patient.height} cm` : <span className="text-slate-300 italic">-</span>}
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Scale className="w-4 h-4 text-slate-400 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-xs font-medium text-slate-400 uppercase tracking-wider">Greutate</p>
                    <p className="text-sm font-medium text-slate-800">
                      {latestWeight ? `${latestWeight} kg` : <span className="text-slate-300 italic">-</span>}
                    </p>
                  </div>
                </div>
              </div>

              {/* Allergies */}
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <AlertTriangle className="w-4 h-4 text-red-500" />
                  <p className="text-xs font-medium text-slate-400 uppercase tracking-wider">Alergii</p>
                </div>
                {patient.allergies ? (
                  <div className="bg-red-50 border border-red-100 rounded-lg px-3 py-2">
                    <p className="text-sm font-medium text-red-700">{patient.allergies}</p>
                  </div>
                ) : (
                  <p className="text-xs text-emerald-600 font-medium">Fără alergii cunoscute</p>
                )}
              </div>

              {/* Observations */}
              {patient.notes && (
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <FileText className="w-4 h-4 text-slate-400" />
                    <p className="text-xs font-medium text-slate-400 uppercase tracking-wider">Observații</p>
                  </div>
                  <p className="text-sm text-slate-600 bg-slate-50 rounded-lg px-3 py-2">{patient.notes}</p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Latest Lab Values Section */}
        {latestConsultation && (latestGlycemia || latestHbA1c || latestHDL || latestLDL || latestTriglycerides) && (
          <div className="mt-8 pt-6 border-t border-slate-100">
            <div className="flex items-center gap-2 mb-4">
              <Activity className="w-4 h-4 text-slate-500" />
              <h3 className="text-sm font-semibold text-slate-600 uppercase tracking-wider">
                Ultimele Valori de Laborator
              </h3>
              <span className="text-xs text-slate-400 font-normal normal-case">
                ({format(new Date(latestConsultation.consultation_date), 'd MMM yyyy', { locale: ro })})
              </span>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3">
              {latestGlycemia && (
                <div className="bg-slate-50 rounded-lg p-3 text-center border border-slate-100">
                  <p className="text-[10px] font-medium text-slate-400 uppercase tracking-wider">Glicemie</p>
                  <p className={`text-lg font-bold ${getValueColor('glycemia', latestGlycemia)}`}>
                    {latestGlycemia}
                  </p>
                  <p className="text-[10px] text-slate-400">mg/dL</p>
                </div>
              )}
              {latestHbA1c && (
                <div className="bg-slate-50 rounded-lg p-3 text-center border border-slate-100">
                  <p className="text-[10px] font-medium text-slate-400 uppercase tracking-wider">HbA1c</p>
                  <p className={`text-lg font-bold ${getValueColor('hba1c', latestHbA1c)}`}>
                    {latestHbA1c}
                  </p>
                  <p className="text-[10px] text-slate-400">%</p>
                </div>
              )}
              {latestHDL && (
                <div className="bg-slate-50 rounded-lg p-3 text-center border border-slate-100">
                  <p className="text-[10px] font-medium text-slate-400 uppercase tracking-wider">HDL</p>
                  <p className={`text-lg font-bold ${getValueColor('hdl', latestHDL)}`}>
                    {latestHDL}
                  </p>
                  <p className="text-[10px] text-slate-400">mg/dL</p>
                </div>
              )}
              {latestLDL && (
                <div className="bg-slate-50 rounded-lg p-3 text-center border border-slate-100">
                  <p className="text-[10px] font-medium text-slate-400 uppercase tracking-wider">LDL</p>
                  <p className={`text-lg font-bold ${getValueColor('ldl', latestLDL)}`}>
                    {latestLDL}
                  </p>
                  <p className="text-[10px] text-slate-400">mg/dL</p>
                </div>
              )}
              {latestTriglycerides && (
                <div className="bg-slate-50 rounded-lg p-3 text-center border border-slate-100">
                  <p className="text-[10px] font-medium text-slate-400 uppercase tracking-wider">Trigliceride</p>
                  <p className={`text-lg font-bold ${getValueColor('triglycerides', latestTriglycerides)}`}>
                    {latestTriglycerides}
                  </p>
                  <p className="text-[10px] text-slate-400">mg/dL</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Active Diagnoses */}
        {patient.diagnoses?.length > 0 && (
          <div className="mt-8 pt-6 border-t border-slate-100">
            <div className="flex items-center gap-2 mb-4">
              <Stethoscope className="w-4 h-4 text-slate-500" />
              <h3 className="text-sm font-semibold text-slate-600 uppercase tracking-wider">
                Diagnostice Active
              </h3>
            </div>
            <div className="flex flex-wrap gap-2">
              {patient.diagnoses.map((diagId) => (
                <Badge key={diagId} variant="secondary" className="text-xs py-1 px-2.5 bg-slate-100 text-slate-700 font-normal">
                  {getDiagnosisName(diagId)}
                </Badge>
              ))}
            </div>
          </div>
        )}

        {/* Statistics Cards - Compact design with neutral colors */}
        <div className="mt-8 pt-6 border-t border-slate-100">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div className="bg-slate-50 rounded-xl p-4 border border-slate-200">
              <div className="flex items-center gap-2 mb-1">
                <Stethoscope className="w-4 h-4 text-slate-400" />
                <p className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider">Consultații</p>
              </div>
              <p className="text-2xl font-bold text-slate-700">{consultations.length}</p>
            </div>
            
            <div className="bg-slate-50 rounded-xl p-4 border border-slate-200">
              <div className="flex items-center gap-2 mb-1">
                <Clock className="w-4 h-4 text-slate-400" />
                <p className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider">Ultima vizită</p>
              </div>
              <p className="text-base font-bold text-slate-700">
                {consultations.length > 0 
                  ? format(new Date(consultations[0].consultation_date), 'd MMM yyyy', { locale: ro })
                  : '-'
                }
              </p>
            </div>
            
            <div className="bg-slate-50 rounded-xl p-4 border border-slate-200">
              <div className="flex items-center gap-2 mb-1">
                <Activity className="w-4 h-4 text-slate-400" />
                <p className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider">Diagnostice</p>
              </div>
              <p className="text-2xl font-bold text-slate-700">{patient.diagnoses?.length || 0}</p>
            </div>
            
            <div className="bg-slate-50 rounded-xl p-4 border border-slate-200">
              <div className="flex items-center gap-2 mb-1">
                <Calendar className="w-4 h-4 text-slate-400" />
                <p className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider">Înregistrat</p>
              </div>
              <p className="text-base font-bold text-slate-700">
                {format(new Date(patient.created_at), 'd MMM yyyy', { locale: ro })}
              </p>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
